import { Dialog } from '@base-ui/react/dialog';
import { useTranslation } from 'react-i18next';
import type { IDocument, IFolder } from '@apis/documents';
import { CloseIcon } from '@/utils/icons';
import { PickCombo } from '@components/ui/PickCombo/PickCombo';
import { folderPathLabel, folderPathOptions } from '../utils/folderPath';
import { sizeErrorMessage } from '../utils/validationMessages';
import { useUploadFlow } from './hooks/useUploadFlow';
import { FilePicker } from './FilePicker';
import { ApprovalToggles } from './ApprovalToggles';
import styles from './UploadModal.module.css';

/**
 * Props for the upload modal component.
 * newVersionTarget: If set, the modal uploads a new version of this document.
 * onUploaded: Callback when the upload is complete, with the destination
 * folderId, whether a requested approve-on-upload was refused, and the name it
 * got when its own was taken (null otherwise).
 */
interface IUploadModalProps {
  open: boolean;
  siteId: string;
  folderId?: string;
  folderName?: string;
  folders: IFolder[];
  newVersionTarget?: IDocument | null;
  canApprove?: boolean; // The caller may approve versions — offers the approve-on-upload box
  onClose: () => void;
  onUploaded: (folderId: string, autoApproveSkipped: boolean, renamedTo: string | null) => void | Promise<void>;
}

/**
 * The upload dialog: pick or drop a file, choose the destination, comment,
 * approval options, upload with progress. All behavior lives in useUploadFlow;
 * this file is the dialog's layout across its modes (plain upload, new version
 * of a document, and the choice when a plain upload's name is already taken).
 */
export function UploadModal({ open, siteId, folderId: initialFolderId, folderName: initialFolderName, folders, newVersionTarget = null, canApprove = false, onClose, onUploaded }: IUploadModalProps) {
  const { t } = useTranslation();

  const flow = useUploadFlow({
    open, siteId, initialFolderId, newVersionTarget, canApprove, onClose, onUploaded,
  });

  // Full path to the destination (e.g. "図面 / Foundation / Rebar /"), with the
  // caller's folder name (or the raw id) as the fallback for an unknown id.
  const resolvedFolderPath =
    folderPathLabel(t, folders, flow.selectedFolderId) ?? `${initialFolderName ?? flow.selectedFolderId} /`;

  return (
    <Dialog.Root open={open} onOpenChange={(isOpen) => { if (!isOpen) flow.handleClose(); }}>
      <Dialog.Portal>
        <Dialog.Backdrop className={styles.backdrop} />
        <Dialog.Popup className={styles.panel}>
          <header className={styles.header}>
            <Dialog.Title className={styles.title}>
              {newVersionTarget
                ? t('documents.upload.newVersionTitle')
                : flow.sameNamed ? t('documents.upload.sameNameTitle') : t('documents.upload.title')}
            </Dialog.Title>
            <Dialog.Close className={styles.closeBtn} aria-label={t('documents.upload.close')}>
              <CloseIcon size={18} />
            </Dialog.Close>
          </header>

          {flow.sameNamed ? (
            <p className={styles.versionMessage}>
              {t('documents.upload.sameNameMessage', { name: flow.sameNamed.name })}
            </p>
          ) : (
            <>
              {newVersionTarget && (
                <p className={styles.versionMessage}>
                  {t('documents.upload.newVersionInfo', { name: newVersionTarget.name })}
                </p>
              )}
              {(initialFolderId || newVersionTarget) ? (
                <div className={`${styles.field} ${styles.destinationField}`} data-tour="documents-upload-destination">
                  <span className={styles.label}>{t('documents.upload.destination')}</span>
                  <span className={styles.folderName}>{resolvedFolderPath}</span>
                </div>
              ) : (
                <div className={styles.field} data-tour="documents-upload-destination">
                  <label className={styles.label} htmlFor="upload-folder">
                    {t('documents.upload.destination')}
                  </label>
                  <PickCombo
                    id="upload-folder"
                    value={flow.selectedFolderId}
                    options={folderPathOptions(t, folders)}
                    placeholder={t('documents.upload.selectFolder')}
                    onPick={flow.setSelectedFolderId}
                    filterable
                    disabled={flow.uploading}
                  />
                </div>
              )}

              <FilePicker
                file={flow.file}
                disabled={flow.uploading}
                accept={flow.requiredType}
                onPick={flow.acceptPickedFile}
                onClear={flow.clearFile}
              />

              <div className={styles.field} data-tour="documents-upload-comment">
                <label className={styles.label} htmlFor="upload-comment">
                  {t('documents.upload.commentLabel')}
                </label>
                <input
                  id="upload-comment"
                  type="text"
                  className={styles.input}
                  value={flow.comment}
                  onChange={(e) => flow.setComment(e.target.value)}
                  placeholder={t(newVersionTarget ? 'documents.upload.commentPlaceholder' : 'documents.upload.commentPlaceholderFirst')}
                  disabled={flow.uploading}
                />
              </div>

              <ApprovalToggles
                canApprove={canApprove}
                submitForApproval={flow.submitForApproval}
                autoApprove={flow.autoApprove}
                awaitingDecision={flow.awaitingDecision}
                disabled={flow.uploading}
                onSubmitForApprovalChange={flow.setSubmitForApproval}
                onAutoApproveChange={flow.setAutoApprove}
              />
            </>
          )}

          {flow.sizeError && (
            <p className={styles.error}>{sizeErrorMessage(t, flow.sizeError)}</p>
          )}

          {flow.uploading && (
            <div
              className={styles.progress}
              role="progressbar"
              aria-label={t(flow.finishing ? 'documents.upload.finishing' : 'documents.upload.uploading')}
              aria-valuemin={0}
              aria-valuemax={100}
              {...(flow.progress === null ? {} : { 'aria-valuenow': Math.round(flow.progress * 100) })}
            >
              <div
                className={`${styles.progressBar} ${flow.progress === null ? styles.progressBarIndeterminate : ''}`}
                style={flow.progress === null ? undefined : { width: `${flow.progress * 100}%` }}
              />
            </div>
          )}

          {flow.error && <p className={styles.error}>{flow.error}</p>}

          <div className={styles.actions}>
            {flow.sameNamed ? (
              <>
                <button type="button" className={styles.cancelBtn} onClick={flow.cancelSameName} disabled={flow.uploading}>
                  {t('documents.upload.back')}
                </button>
                <button type="button" className={styles.cancelBtn} onClick={flow.uploadSeparately} disabled={flow.uploading}>
                  {t('documents.upload.sameNameSeparate')}
                </button>
                <button type="button" className={styles.confirmBtn} onClick={flow.uploadAsVersion} disabled={flow.uploading}>
                  {t('documents.upload.sameNameVersion')}
                </button>
              </>
            ) : (
              <>
                <Dialog.Close className={styles.cancelBtn}>
                  {t('documents.upload.cancel')}
                </Dialog.Close>
                <button
                  type="button"
                  className={styles.confirmBtn}
                  onClick={flow.handleConfirm}
                  disabled={!flow.file || !flow.selectedFolderId || flow.uploading || flow.checkingExisting || !!flow.sizeError}
                >
                  {flow.finishing
                    ? t('documents.upload.finishing')
                    : flow.uploading
                      ? t('documents.upload.uploading')
                      : t('documents.upload.confirm')}
                </button>
              </>
            )}
          </div>
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}